import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';

const WIDGET_UUID = '965317ab-ff19-48f0-87c5-78595fdeb8a3';
// Public read-only key — the identical key ReviewForest's own embed script
// (widgets.reviewforest.org/main.js) ships to every visitor's browser.
const API_KEY = 'YXDjAJhJReHVdZvx';

let cachedData = null;
let pendingFetch = null;

function fetchReviewData() {
  if (cachedData) return Promise.resolve(cachedData);
  if (!pendingFetch) {
    pendingFetch = fetch(`https://api.reviewforest.org/v1/widgets?uuids=${WIDGET_UUID}`, {
      headers: { apikey: API_KEY },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((json) => {
        const d = json?.data?.[0]?.data;
        if (!d) throw new Error('no widget data');
        cachedData = {
          score: parseFloat(d.score),
          count: d.reviewAmountOnPlatform ?? d.reviewAmount,
          url: `https://reviewforest.org/${d.slug}`,
        };
        return cachedData;
      })
      .catch(() => {
        pendingFetch = null;
        return null;
      });
  }
  return pendingFetch;
}

function Stars({ score }) {
  const pct = Math.max(0, Math.min(100, (score / 5) * 100));
  const row = (colorClass) => (
    <span className={`flex gap-0.5 ${colorClass}`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className="w-4 h-4 shrink-0" fill="currentColor" stroke="none" />
      ))}
    </span>
  );
  return (
    <span className="relative inline-flex shrink-0" aria-hidden="true">
      {row('text-muted-foreground/30')}
      <span className="absolute inset-0 overflow-hidden" style={{ width: `${pct}%` }}>
        {row('text-amber-400')}
      </span>
    </span>
  );
}

export function ReviewForestWidget({ className = '', stacked = false }) {
  const [data, setData] = useState(cachedData);

  useEffect(() => {
    if (!cachedData) fetchReviewData().then((d) => d && setData(d));
  }, []);

  if (!data) return null;

  return (
    <div className={`flex justify-center ${className}`}>
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`group ${stacked ? 'inline-flex flex-col items-center gap-1.5' : 'inline-flex items-center gap-3'}`}
        aria-label={`${data.score.toFixed(1)} out of 5 stars – read our customer reviews`}
        title="Customer reviews"
        data-testid="reviewforest-widget"
      >
        <span className="inline-flex items-center gap-2">
          <span className="text-xl font-black leading-none text-foreground">
            {data.score.toFixed(1)}
          </span>
          <Stars score={data.score} />
        </span>
        <span
          className={`text-muted-foreground group-hover:text-foreground transition-colors underline underline-offset-2 whitespace-nowrap ${stacked ? 'text-xs' : 'text-sm'}`}
        >
          Customer reviews ({data.count})
        </span>
      </a>
    </div>
  );
}
